import { Box, Button } from '@material-ui/core';
import Build from '@material-ui/icons/Build';
import Check from '@material-ui/icons/Check';
import Delete from '@material-ui/icons/Delete';
import LocationSearching from '@material-ui/icons/LocationSearching';
import MyLocation from '@material-ui/icons/MyLocation';
import Refresh from '@material-ui/icons/Refresh';
import Save from '@material-ui/icons/Save';
import { Alert } from '@material-ui/lab';
import { latLng, LatLng } from 'leaflet';
import {
	BaseSyntheticEvent,
	ReactElement,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { GoogleMapsLocationSelector, MapViewer } from '..';
import { useCallbackSafeState } from '../../../hooks';
import {
	MapDetailDto,
	TopographyDto,
	TopographyMetadataDto
} from '../../../models';
import {
	AppState,
	AppThunkDispatch,
	fetchMetadataByPage,
	Licence,
	MapAction,
	MetadataActions,
	Topography,
	TopographyActions,
	topographyCreate,
	topographyUpdate,
	topographyVerify
} from '../../../redux';
import { convertToLatLng } from '../../../utils';
import {
	Body1,
	MapViewerClickHandler,
	TextInputField,
	TooltipIconButton
} from '../../atoms';
import { LeafletIconPin } from '../../atoms/leaflet-icon-pin/leaflet-icon-pin.component';
import { FormValidationErrors } from '../../molecules';

import './topographic-form.styles.scss';

export interface ITopographicFormProps {
	topography: OptionalIdTopo;
	licences: Licence[];
	onDelete: (id: string) => void;
}

export interface ITopographicFormStateProps {
	metadata?: TopographyMetadataDto;
	updateTopo: (topography: TopographyDto) => void;
	createTopo: (
		name: string,
		lat: string,
		lng: string,
		page: number,
		letter?: string,
		suffix?: string,
		metadataId?: string
	) => void;
	findMetadata: (page: number, letter: string) => void;
	verifyTopography: (id: string) => void;
}

export type OptionalIdTopo = Omit<Topography, 'id'> & { id?: string };

function TopographicFormView(
	props: ITopographicFormProps & ITopographicFormStateProps
): ReactElement {
	const {
		metadata,
		topography,
		updateTopo,
		createTopo,
		onDelete,
		findMetadata,
		verifyTopography
	} = props;
	const {
		control,
		reset,
		handleSubmit,
		getValues,
		setValue,
		watch,
		formState: { errors, submitCount, isValid, isDirty }
	} = useForm<OptionalIdTopo>({
		defaultValues: {
			...topography,
			suffix: topography.suffix ?? '',
			lat: topography.lat ? topography.lat : '',
			lng: topography.lng ? topography.lng : ''
		}
	});

	const { t } = useTranslation('contentManager');

	const [getTopoPosition, setTopoPosition] = useCallbackSafeState<
		LatLng | undefined
	>(createLatLngFromProps());
	const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
	const page: number = watch().page;
	const section: string | undefined = watch().letter;
	const lat: string | undefined = watch().lat;
	const lng: string | undefined = watch().lng;

	const isVerifiable: boolean = useMemo<boolean>(() => {
		return (
			isValid && !!topography.id && !isDirty && !!topography.metadataId
		);
	}, [topography, isValid, isDirty]);

	useEffect(() => {
		reset({
			...topography,
			suffix: topography.suffix ?? '',
			lat: topography.lat ? topography.lat : '',
			lng: topography.lng ? topography.lng : ''
		});
		findMetadata(topography.page, topography.letter || '');
		setTopoPosition(createLatLngFromProps());
	}, [topography]);

	useEffect(() => {
		findMetadata(page, section || '');
	}, [page, section]);

	useEffect(() => {
		setValue('metadataId', metadata?.id);
	}, [metadata]);

	useEffect(() => {
		if (lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
			const latNumber: number = Number(lat);
			const lngNumber: number = Number(lng);

			setTopoPosition(latLng(latNumber, lngNumber));
		}
	}, [lat, lng]);

	function createLatLngFromProps(): LatLng | undefined {
		if (
			topography.lat &&
			topography.lng &&
			!isNaN(Number(topography.lat)) &&
			!isNaN(Number(topography.lng))
		) {
			const lat: number = Number(topography.lat);
			const lng: number = Number(topography.lng);

			return latLng(lat, lng);
		}
	}

	function resetForm(): void {
		reset({
			...topography,
			suffix: topography.suffix ?? '',
			lat: topography.lat ? topography.lat : '',
			lng: topography.lng ? topography.lng : ''
		});
		setTopoPosition(createLatLngFromProps());
	}

	function onFormSubmit(
		data: OptionalIdTopo,
		event?: BaseSyntheticEvent
	): void {
		if (event) event.stopPropagation();
		if (data.id) {
			updateTopo(data as TopographyDto);
		} else {
			createTopo(
				data.name!,
				data.lat!,
				data.lng!,
				data.page,
				data.letter,
				data.suffix,
				data.metadataId
			);
		}
	}

	function setStatePosition(latlng: LatLng): void {
		setTopoPosition(latlng);
		reset({
			...getValues(),
			lat: `${latlng.lat}`,
			lng: `${latlng.lng}`
		});
	}

	function setStatePositionAndCloseDialog(position: LatLng): void {
		setStatePosition(position);
		closeSearchDialog();
	}

	function openSearchDialog(): void {
		setIsSearchOpen(true);
	}

	function closeSearchDialog(): void {
		setIsSearchOpen(false);
	}

	function deleteTopography(): void {
		if (!topography.id) return;
		onDelete(topography.id);
	}

	function onMapClicked(latlng: LatLng): void {
		if (getTopoPosition()) return;
		setTopoPosition(latlng);
	}

	function onVerify(): void {
		if (topography.id) verifyTopography(topography.id);
	}

	return (
		<form
			className="topographic-form"
			onSubmit={handleSubmit(onFormSubmit)}
		>
			<Box className="topographic-form-container">
				<Box className="general-data">
					<Box className="name-search-field">
						<TextInputField
							control={control}
							name="name"
							label={t('topography.formFields.name')}
							required={t('topography.formFields.requiredName')}
						/>
						{metadata && (
							<TooltipIconButton
								tooltip={t(
									'topography.formFields.searchGoogle'
								)}
								onClick={openSearchDialog}
								icon={<LocationSearching />}
								placement="top"
							/>
						)}
					</Box>
					<TextInputField
						control={control}
						name="page"
						type="number"
						label={t('topography.formFields.page')}
						required={t('topography.formFields.requiredPageNumber')}
					/>
					<TextInputField
						control={control}
						name="letter"
						label={t('topography.formFields.section')}
					/>
				</Box>
				<Box className="location">
					<TextInputField
						control={control}
						name="suffix"
						label={t('topography.formFields.addition')}
					/>
					<TextInputField
						control={control}
						name="lat"
						type="number"
						label={t('topography.formFields.latitude')}
						required={t('topography.formFields.requiredLatitude')}
					/>
					<TextInputField
						control={control}
						name="lng"
						type="number"
						label={t('topography.formFields.longitude')}
						required={t('topography.formFields.requiredLongitude')}
					/>
				</Box>
			</Box>
			{!topography.metadataId && topography.id && (
				<Body1 className="topo-warning">
					{t('topography.linkNotMade')}
				</Body1>
			)}
			<Box>
				<Button onClick={resetForm} variant="outlined" color="primary">
					<Refresh />
					{t('topography.buttons.resetForm')}
				</Button>
				{topography.id && (
					<Button
						onClick={deleteTopography}
						className="delete-button"
						variant="contained"
					>
						<Delete />
						{t('topography.buttons.delete')}
					</Button>
				)}
				<Button type="submit" color="primary" variant="contained">
					<Save />
					{t('save')}
				</Button>
				{topography.verified ? (
					<Button
						color="primary"
						variant="outlined"
						onClick={onVerify}
					>
						<Build />
						{t('topography.buttons.onRepair')}
					</Button>
				) : (
					<Button
						color="primary"
						variant="outlined"
						onClick={onVerify}
						disabled={!isVerifiable}
					>
						<Check />
						{t('topography.buttons.verified')}
					</Button>
				)}
			</Box>
			<FormValidationErrors errors={errors} submitCount={submitCount} />
			{metadata && metadata.id !== '' ? (
				<MapViewer
					casperId={metadata.casperId!}
					mapId={metadata.id}
					projection={metadata.projection}
					corner={convertToLatLng(metadata.corner)}
					oppositeCorner={convertToLatLng(metadata.oppositeCorner)}
				>
					<MapViewerClickHandler onClick={onMapClicked} />
					{getTopoPosition() && (
						<LeafletIconPin
							position={getTopoPosition()!}
							icon={<MyLocation />}
							draggable
							onDragEnd={setStatePosition}
						/>
					)}
				</MapViewer>
			) : (
				<Box className="no-map-info">
					<Alert severity="info">{t('topography.noMapFound')}</Alert>
				</Box>
			)}
			{metadata && (
				<GoogleMapsLocationSelector
					open={isSearchOpen}
					map={metadata as MapDetailDto}
					searchValue={getValues().name ? getValues().name! : ''}
					onSelect={setStatePositionAndCloseDialog}
					onClose={closeSearchDialog}
				/>
			)}
		</form>
	);
}

function mapStateToProps(state: AppState) {
	return {
		metadata: state.metadata.topographyMetadata
	};
}

function mapDispatchToProps(
	dispatch: AppThunkDispatch<TopographyActions | MapAction | MetadataActions>
) {
	return {
		updateTopo: (topography: TopographyDto) =>
			dispatch(topographyUpdate(topography)),
		createTopo: (
			name: string,
			lat: string,
			lng: string,
			page: number,
			letter?: string,
			suffix?: string,
			metadataId?: string
		) =>
			dispatch(
				topographyCreate(
					name,
					lat,
					lng,
					page,
					letter,
					suffix,
					metadataId
				)
			),
		findMetadata: (page: number, letter: string) =>
			dispatch(fetchMetadataByPage(page, letter)),
		verifyTopography: (id: string) => dispatch(topographyVerify(id))
	};
}

export const TopographicForm = connect(
	mapStateToProps,
	mapDispatchToProps
)(TopographicFormView);
